export type AdminPageFilterKind = 'us-version' | 'fixed-noindex' | 'suspicious-not-found';

export interface AdminPageFilterRecord {
  slug?: string;
  canonicalUrl?: string;
  url?: string;
  path?: string;
  country?: string;
  countryCode?: string;
  locale?: string;
  version?: string;
  indexed?: boolean;
  isPermanentNoIndex?: boolean;
  permanentNoIndex?: boolean;
  isFixedNoIndex?: boolean;
  notFoundReason?: string;
  suspiciousReason?: string;
}

export interface AdminRedirectRecord {
  _id?: string;
  sourceUrl?: string;
  destinationUrl?: string;
  type?: 301 | 302 | 410;
  isActive?: boolean;
}

const US_FIELD_VALUES = new Set(['us', 'usa', 'united-states', 'united states', 'en-us']);

function isInvisibleAppRouteSegment(segment: string) {
  return segment.startsWith('(') && segment.endsWith(')');
}

export function normalizeAdminPagePath(value?: string) {
  const raw = String(value || '').trim().replace(/\\/g, '/');
  if (!raw) return '';

  let pathname = raw;
  try {
    const parsed = new URL(raw);
    pathname = parsed.pathname;
  } catch {
    pathname = raw.split(/[?#]/)[0] || '';
  }

  return pathname
    .replace(/\/{2,}/g, '/')
    .split('/')
    .map((segment) => segment.trim())
    .filter((segment) => segment && !isInvisibleAppRouteSegment(segment))
    .join('/')
    .toLowerCase();
}

export function isUsVersionPage(page: AdminPageFilterRecord) {
  const countryCandidates = [
    page.country,
    page.countryCode,
    page.locale,
    page.version,
  ].map((value) => String(value || '').trim().toLowerCase());

  if (countryCandidates.some((value) => US_FIELD_VALUES.has(value))) {
    return true;
  }

  const pathCandidates = [
    page.slug,
    page.canonicalUrl,
    page.url,
    page.path,
  ].map(normalizeAdminPagePath);

  return pathCandidates.some((path) => path === 'us' || path.startsWith('us/'));
}

export function isPermanentNoIndexPage(page: AdminPageFilterRecord) {
  return (
    page.isPermanentNoIndex === true ||
    page.permanentNoIndex === true ||
    page.isFixedNoIndex === true
  );
}

export function isSuspiciousNotFoundPage(page: AdminPageFilterRecord) {
  return Boolean(
    String(page.notFoundReason || '').trim() ||
    String(page.suspiciousReason || '').trim()
  );
}

function addIfPresent(slugs: Set<string>, value?: string) {
  const normalized = normalizeAdminPagePath(value);
  if (normalized) slugs.add(normalized);
}

export function buildAdminPageExistenceSet(slugs: string[]) {
  const existingSlugs = new Set<string>();

  slugs.forEach((slug) => {
    addIfPresent(existingSlugs, slug);
  });

  return existingSlugs;
}

export function getAdminPagePathCandidates(page: AdminPageFilterRecord) {
  const seen = new Set<string>();
  const candidates: string[] = [];

  [page.slug, page.canonicalUrl, page.url, page.path].forEach((value) => {
    const normalized = normalizeAdminPagePath(value);
    if (normalized && !seen.has(normalized)) {
      seen.add(normalized);
      candidates.push(normalized);
    }
  });

  return candidates;
}

export function hasExistingAdminPagePath(
  page: AdminPageFilterRecord,
  existingSlugs: Set<string>
) {
  return getAdminPagePathCandidates(page)
    .some((path) => existingSlugs.has(path));
}

export function getAdminPageNotFoundReason(
  page: AdminPageFilterRecord,
  existingSlugs: Set<string>
) {
  const candidates = getAdminPagePathCandidates(page);
  if (candidates.length === 0) {
    return 'URL missing';
  }

  const missingPath = candidates.find((path) => !existingSlugs.has(path));
  if (missingPath) {
    return 'Folder not found';
  }

  return undefined;
}

export function getRedirectsAppliedToPage(
  page: AdminPageFilterRecord,
  redirects: AdminRedirectRecord[]
) {
  const pagePaths = new Set(getAdminPagePathCandidates(page));
  if (pagePaths.size === 0) return [];

  return redirects.filter((redirect) => {
    if (redirect.isActive === false) return false;
    const sourcePath = normalizeAdminPagePath(redirect.sourceUrl);
    return sourcePath ? pagePaths.has(sourcePath) : false;
  });
}
